import { getMostRecentFieldValue, getNextUuid, getNestedSubelementsByType } from '@fs/zion-sls'

const commonFactTypes = new Set([
  'AGE',
  'RACE_OR_COLOR',
  'OCCUPATION',
  'BUR_METHOD',
  'CLAN',
  'EDUCATION',
  'ETHNICITY',
  'MARITAL_STATUS',
  'MIL_RANK',
  'NATIONALITY',
  'RELIGION',
  'TRIBE_OR_CLAN',
])

export const suggestedFactTypes = ['AGE', 'OCCUPATION']

export const filterOutTheseFactTypes = [
  'SEX_CODE',
  'AGE_HOURS',
  'AGE_DAYS',
  'AGE_MINUTES',
  'AGE_MONTHS',
  'AGE_SECONDS',
  'AGE_WEEKS',
  'AGE_YEARS',
]

/**
 * Determines if a fact type is valid for use as an additional fact.
 * A valid additional fact must be editable, displayable, and applicable to a person element.
 *
 * @param {Object} type - The fact type object to validate
 * @param {boolean} type.editable - Whether the fact type can be edited
 * @param {boolean} type.displayable - Whether the fact type should be displayed
 * @param {string[]} type.elementTypes - Array of element types this fact can be associated with
 * @returns {boolean} True if the type meets all criteria for an additional fact
 */
export const isValidAdditionalFactType = (type) => {
  return Boolean(
    type?.editable && // Only include editable facts
    type?.displayable && //  Only include displayable facts
    (type?.elementTypes?.includes('PERSON') || type?.fieldType === 'AGE') // We need to show AGE even if it's not marked as applicable to PERSON
  )
}

export const getAdditionalFactOptions = (types) => {
  const commonFacts = []
  const otherFacts = []

  types.fields.forEach((type) => {
    if (
      !filterOutTheseFactTypes.includes(type.fieldType) && // Filter out unwanted fact types
      isValidAdditionalFactType(type) // Ensure the fact type is valid for additional facts
    ) {
      if (commonFactTypes.has(type.fieldType)) {
        commonFacts.push({
          label: type.displayLabel,
          value: type.fieldType,
        })
      } else {
        otherFacts.push({
          label: type.displayLabel,
          value: type.fieldType,
        })
      }
    }
  })

  // Deduplicate arrays based on the value field
  const deduplicateByValue = (arr) => {
    const seen = new Set()
    return arr.filter((item) => {
      if (seen.has(item.value)) {
        return false
      }
      seen.add(item.value)
      return true
    })
  }

  return {
    commonFacts: deduplicateByValue(commonFacts),
    otherFacts: deduplicateByValue(otherFacts),
  }
}

const prepareAgeFieldDefaultValues = (person) => {
  const ageElement = getNestedSubelementsByType(person, 'AGE')?.[0] || null
  const ageField = ageElement?.subElements ? getNestedSubelementsByType(ageElement, 'AGE', true)?.[0] : ageElement
  const ageValue = getMostRecentFieldValue(ageField) || ''
  const ageLinkedPersonIds = ageElement ? ageElement.superElements : ageField?.superElements || []

  if (!ageElement || !ageField) return null

  return {
    label: ageField?.displayLabel || ageElement?.displayable,
    value: ageValue,
    key: ageField?.fieldType || ageElement?.fieldType || 'AGE',
    id: ageField?.id || ageElement?.id || getNextUuid(),
    superElements: ageField?.superElements || [],
    editable: ageField?.typeData?.editable ?? true,
    dispatches: [],
    shared: ageLinkedPersonIds.length > 1,
    linkedPersonIds: ageLinkedPersonIds,
    added: !ageField?.id && !ageElement?.id, // If there's no ID, it's a newly added fact
  }
}

export const prepareAdditionalFactDefaultValues = (additionalFacts, person) => {
  const ageField = prepareAgeFieldDefaultValues(person)

  // Process remaining facts, excluding SEX_CODE and AGE if they exist in additionalFacts
  const otherFacts = additionalFacts
    .filter((fact) => !['SEX_CODE', 'AGE'].includes(fact?.fieldType))
    .map((fact) => ({
      label: fact.displayLabel,
      value: getMostRecentFieldValue(fact) || '',
      key: fact.fieldType,
      id: fact.id,
      superElements: fact.superElements,
      editable: fact?.typeData?.editable ?? true,
      dispatches: [],
      shared: fact.superElements?.length > 1,
      added: !fact.id, // If there's no ID, it's a newly added fact
      linkedPersonIds: fact.superElements,
    }))

  // SEX_CODE is always first, followed by other facts
  return { additionalFacts: [ageField, ...otherFacts].filter(Boolean) } // Filter out any null values
}
